import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { type Language, t, type TranslationKey } from '@/lib/i18n'
import { setUiLang } from '@/i18n'
import { UiLangContext } from '@/i18n/uiLangContext'
import { setUiLanguage } from '@teach/ui'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: TranslationKey) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const SUPPORTED: Language[] = ['ru', 'en']
const isSupported = (value: string | null): value is Language =>
  value !== null && (SUPPORTED as string[]).includes(value)

const detectFromBrowser = (): Language => {
  if (typeof navigator === 'undefined') return 'ru'
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const raw of candidates) {
    const lower = raw.toLowerCase()
    if (lower.startsWith('ru')) return 'ru'
    if (lower.startsWith('en')) return 'en'
  }
  // Браузер ни на русском, ни на английском — английский: он понятнее
  // большинству, чем русский (решение заказчика).
  return 'en'
}

const resolveInitialLanguage = (): Language => {
  if (typeof window === 'undefined') return 'ru'
  const stored = localStorage.getItem('language')
  if (isSupported(stored)) return stored
  return detectFromBrowser()
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(resolveInitialLanguage)

  useEffect(() => {
    localStorage.setItem('language', language)
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
  }

  const translate = (key: TranslationKey) => t(key, language)
  // Модульный язык (`messagesNow`, `uiLocale`) — синхронно, ДО рендера детей.
  setUiLang(language)
  setUiLanguage(language)
  // Язык страницы = язык интерфейса. С захардкоженным `lang="en"` Chrome считал
  // русскую студию английской и автоматически «переводил» её на русский —
  // переводчик портил уже русский текст («Создать курс» → «рестоф»). Синхронно,
  // до первой отрисовки: браузер решает про перевод сразу после загрузки.
  if (typeof document !== 'undefined') document.documentElement.lang = language

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translate }}>
      <UiLangContext.Provider value={language}>{children}</UiLangContext.Provider>
    </LanguageContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- context+hook co-location is a valid React pattern
export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
